"use client";

import { useState } from "react";
import styles from "./credit-calculator.module.css";
import { useTranslations } from "next-intl";

export default function CreditCalculator() {
  const [amount, setAmount] = useState(0);
  const t = useTranslations("AgencyPricing.CreditCalculator");

  const { baseCredits, bonusCredits, totalCredits, bonusPercent } =
    calculateCredits(amount);
  const listingEquivalents = calculateListingEquivalents(totalCredits);

  return (
    <div className={styles.calculator}>
      <h4 className={styles.title}>{t("title")}</h4>

      <div className={styles.inputGroup}>
        <div className={styles.currencyWrapper}>
          <input
            type="number"
            value={amount === 0 ? "" : amount}
            onChange={(e) => setAmount(Number(e.target.value))}
            placeholder={t("inputPlaceholder")}
            className={styles.input}
          />
        </div>

        <input
          type="range"
          min="0"
          max="5000"
          step="50"
          value={amount}
          onChange={(e) => setAmount(Number(e.target.value))}
          className={styles.rangeSlider}
        />
      </div>

      {amount >= 0 && (
        <div className={styles.results}>
          <div className={styles.creditsWrapper}>
            <p className={styles.textRow}>
              <span>{t("baseCredits")}</span>
              <span className={styles.value}>{baseCredits}</span>
            </p>
            <p className={styles.textRow}>
              <span>
                {t("bonus")} {bonusPercent > 0 && `(${bonusPercent}%)`}
              </span>
              <span className={styles.value}>+{bonusCredits}</span>
            </p>

            <div className={styles.divider}></div>

            <p className={`${styles.textRow} ${styles.totalRow}`}>
              <span>{t("totalCredits")}</span>
              <span className={styles.value}>{totalCredits}</span>
            </p>
          </div>

          <h4 className={styles.subTitle}>{t("subtitle")}</h4>
          <ul className={styles.listingsList}>
            <li className={styles.listItem}>
              <span className={styles.equals}>=</span>
              <span className={styles.count}>
                {listingEquivalents.essential}
              </span>
              <span className={styles.type}>Essential {t("listings")}</span>
            </li>
            <li className={styles.listItem}>
              <span className={styles.equals}>=</span>
              <span className={styles.count}>{listingEquivalents.plus}</span>
              <span className={styles.type}>Plus {t("listings")}</span>
            </li>
            <li className={styles.listItem}>
              <span className={styles.equals}>=</span>
              <span className={styles.count}>{listingEquivalents.prime}</span>
              <span className={styles.type}>Prime {t("listings")}</span>
            </li>
            <li className={styles.listItem}>
              <span className={styles.equals}>=</span>
              <span className={styles.count}>
                {listingEquivalents.spotlight}
              </span>
              <span className={styles.type}>Spotlight {t("listings")}</span>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}

const getBonusPercent = (amount: number) => {
  if (amount >= 4000) return 25;
  if (amount >= 2000) return 20;
  if (amount >= 1000) return 15;
  if (amount >= 500) return 10;
  if (amount > 0) return 5;
  return 0;
};

const calculateCredits = (amount: number) => {
  const bonusPercent = getBonusPercent(amount);
  const baseCredits = amount;
  const bonusCredits = (baseCredits * bonusPercent) / 100;
  return {
    baseCredits,
    bonusCredits,
    totalCredits: baseCredits + bonusCredits,
    bonusPercent,
  };
};

const listingCosts = {
  essential: 9,
  plus: 19,
  prime: 39,
  spotlight: 79,
};

const calculateListingEquivalents = (credits: number) => {
  return {
    essential: Math.floor(credits / (listingCosts.essential / 2)),
    plus: Math.floor(credits / (listingCosts.plus / 2)),
    prime: Math.floor(credits / (listingCosts.prime / 2)),
    spotlight: Math.floor(credits / (listingCosts.spotlight / 2)),
  };
};
